@use '../../style/functions';
@use '../../style/mixins';

:host {
    display: inline-block;
}

:host([hidden]) {
    display: none;
}

@include mixins.visualize-aria-expanded('button');

button.mdc-button {
    // As long as this component is depended on MDC,
    // we need to force it to be font-agnostic.
    // When MDC-dependency is removed, this block can also be removed.
    // However, on removal of MDC-dependency, we should also make sure to check
    // other font-related styles that might be set by MDC,
    // such as `letter-spacing` or `font-size`.
    font-family: inherit;
}

button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.125rem;

    border: none;
    border-radius: 0.4rem;
    padding: {
        top: 0;
        right: var(--button-padding-right, 0.5rem);
        bottom: 0;
        left: var(--button-padding-left, 0.5rem);
    }

    min-height: 2.25rem;
    width: 100%;

    &:disabled {
        cursor: not-allowed;

        &.outlined {
            border-color: rgba(var(--contrast-1700), 0.2);
        }
    }

    &:not(:disabled) {
        @include mixins.visualize-keyboard-focus;
        @include mixins.is-elevated-clickable();
    }

    :host(limel-button[primary]) & {
        &:not(:disabled) {
            color: var(
                --lime-on-primary-color,
                var(--limel-theme-on-primary-color)
            );
            background-color: var(
                --lime-primary-color,
                var(--limel-theme-primary-color)
            );
        }
        &:disabled {
            background-color: rgba(var(--contrast-1700), 0.15);
        }
    }

    :host(limel-button:not([primary])) & {
        &:not(:disabled) {
            color: var(--lime-primary-color, var(--limel-theme-primary-color));
        }
        &:disabled {
            color: rgba(var(--contrast-1600), 0.37);
            background-color: rgba(var(--contrast-1600), 0.1);
        }
    }
}

.label {
    font-size: var(--limel-theme-default-font-size);
    font-weight: 600;
    letter-spacing: functions.pxToRem(0.5);
    padding: 0 0.25rem;
}

limel-spinner {
    position: absolute;
}

limel-icon {
    width: 1.25rem;
    flex-shrink: 0;
    vertical-align: top;
}

svg {
    height: functions.pxToRem(30);
    opacity: 0;
    pointer-events: none;
    position: absolute;
    width: functions.pxToRem(30);

    line {
        stroke: rgb(var(--contrast-600));
        stroke-width: 2;
    }
}

limel-icon,
.label,
limel-spinner,
svg {
    transition: opacity 300ms ease-in-out;
}

.loading {
    limel-icon,
    .label {
        opacity: 0;
    }
    limel-spinner {
        opacity: 1;
    }
}

.just-loaded,
.just-failed {
    limel-icon,
    .label {
        opacity: 0;
    }
    svg {
        opacity: 1;
    }
}

.just-failed {
    background-color: var(--limel-theme-error-color) !important;
    animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

.outlined {
    border: 1px solid;
    border-color: var(--lime-primary-color, var(--limel-theme-primary-color));
}

@keyframes shake {
    10%,
    90% {
        transform: translate3d(#{functions.pxToRem(-1)}, 0, 0);
    }

    20%,
    80% {
        transform: translate3d(#{functions.pxToRem(2)}, 0, 0);
    }

    30%,
    50%,
    70% {
        transform: translate3d(#{functions.pxToRem(-3)}, 0, 0);
    }

    40%,
    60% {
        transform: translate3d(#{functions.pxToRem(3)}, 0, 0);
    }
}

@import './partial-styles/_has-reduced-presence';
